import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, ListGroup, ListGroupItem, Badge } from 'reactstrap';
import { toastr } from 'react-redux-toastr';
import { change } from 'redux-form';
import BaggageQRModalForm, { BAGGAGE_QR_FORM } from './BaggageQRModalForm';
import { TICKET_STATUS } from '../../../../config/constants';

const STATUS = {
  LOADING: 'loading',
  VALID: 'valid',
  INVALID: 'invalid',
  DIFFERENT_ITINERARY: 'different_itinerary',
};

const BaggageQRScannerModal = ({
  onConfirm,
  onCancel,
  validateTicket,
  loading,
  dispatchChange,
  defaultItineraryId,
}) => {
  const [scannedList, setScannedList] = useState([]);

  const anchorItineraryRef = useRef(
    defaultItineraryId !== undefined && defaultItineraryId !== null
      ? defaultItineraryId
      : null,
  );

  const handleDuplicateScan = (code) => {
    toastr.warning(
      'Boleto ya escaneado',
      `El código ${code} ya ha sido escaneado.`,
      {
        disableCloseButtonFocus: true,
      },
    );
  };

  const handleScan = ({ serieCorrelative }) => {
    if (!serieCorrelative) return;

    const alreadyScanned = scannedList.some(
      (item) => item.code === serieCorrelative,
    );

    dispatchChange(BAGGAGE_QR_FORM, 'serieCorrelative', '');

    const input = document.querySelector('.qr-series-correlative');
    if (input) input.focus();

    if (alreadyScanned) {
      handleDuplicateScan(serieCorrelative);
      return;
    }

    setScannedList((prev) => [
      ...prev,
      {
        code: serieCorrelative,
        status: STATUS.LOADING,
        data: null,
        errorMessage: null,
      },
    ]);

    validateTicket(serieCorrelative)
      .then((ticketData) => {
        if (ticketData.ticketStatus !== TICKET_STATUS.ACTIVE.value) {
          throw new Error('Boleto no válido');
        }

        if (anchorItineraryRef.current === null) {
          anchorItineraryRef.current = ticketData.itineraryId;
        }

        if (ticketData.itineraryId !== anchorItineraryRef.current) {
          setScannedList((prev) =>
            prev.map((item) =>
              item.code === serieCorrelative
                ? {
                    ...item,
                    status: STATUS.DIFFERENT_ITINERARY,
                    data: ticketData,
                    errorMessage: 'Pertenece a otro itinerario',
                  }
                : item,
            ),
          );
          return;
        }

        setScannedList((prev) =>
          prev.map((item) =>
            item.code === serieCorrelative
              ? { ...item, status: STATUS.VALID, data: ticketData }
              : item,
          ),
        );
      })
      .catch((error) => {
        setScannedList((prev) =>
          prev.map((item) =>
            item.code === serieCorrelative
              ? {
                  ...item,
                  status: STATUS.INVALID,
                  errorMessage: error.message || 'Boleto no encontrado',
                }
              : item,
          ),
        );
      });
  };

  const handleRemove = (code) => {
    setScannedList((prev) => prev.filter((item) => item.code !== code));
  };

  const handleConfirm = () => {
    const validTickets = scannedList
      .filter((item) => item.status === STATUS.VALID)
      .map((item) => item.data);

    onConfirm(validTickets);
  };

  const validCount = scannedList.filter(
    (item) => item.status === STATUS.VALID,
  ).length;

  const renderStatusBadge = (item) => {
    if (item.status === STATUS.LOADING)
      return (
        <Badge color="secondary">
          <i className="fa fa-spinner fa-spin" /> Verificando...
        </Badge>
      );
    if (item.status === STATUS.VALID)
      return <Badge color="success">✓ Válido</Badge>;
    if (item.status === STATUS.DIFFERENT_ITINERARY)
      return (
        <Badge color="warning" title={item.errorMessage}>
          ⚠ {item.errorMessage}
        </Badge>
      );
    return (
      <Badge color="danger" title={item.errorMessage}>
        ✗ {item.errorMessage}
      </Badge>
    );
  };

  const renderColor = (status) => {
    if (status === STATUS.VALID) return 'success';
    if (status === STATUS.INVALID) return 'danger';
    if (status === STATUS.DIFFERENT_ITINERARY) return 'warning';
    return '';
  };

  const hasLoading = scannedList.some((item) => item.status === STATUS.LOADING);

  return (
    <div>
      <BaggageQRModalForm onSubmit={handleScan} />

      {scannedList.length > 0 && (
        <div className="mt-3">
          <h6>Boletos escaneados ({scannedList.length})</h6>
          <ListGroup>
            {scannedList.map((item) => (
              <ListGroupItem
                key={item.code}
                className="d-flex justify-content-between align-items-center"
                color={renderColor(item.status)}
              >
                <span>
                  <strong>{item.code}</strong>
                </span>
                <div className="d-flex align-items-center gap-2">
                  {renderStatusBadge(item)}
                  <Button
                    type="button"
                    color="link"
                    size="sm"
                    className="text-danger p-0 ml-2"
                    onClick={() => handleRemove(item.code)}
                  >
                    <i className="fa fa-times" />
                  </Button>
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        </div>
      )}

      <div className="mt-3 d-flex justify-content-end gap-2">
        <Button
          type="button"
          color="secondary"
          outline
          onClick={onCancel}
          className="mr-2"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          color="primary"
          disabled={validCount === 0 || hasLoading || loading}
          onClick={handleConfirm}
        >
          Agregar {validCount > 0 ? `(${validCount})` : ''} al formulario
        </Button>
      </div>
    </div>
  );
};

BaggageQRScannerModal.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  validateTicket: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  defaultItineraryId: PropTypes.number,
};

BaggageQRScannerModal.defaultProps = {
  loading: false,
  defaultItineraryId: null,
};

const mapStateToProps = ({ ReservationUnit }) => ({
  loading: ReservationUnit.Ticket.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchChange: change,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(BaggageQRScannerModal);
